/** Header-action builder for the Project Planner (Gantt) tab.
 *  Owns: Today, Subcontractor filter, programme timescale,
 *  Search toggle, + create menu (Task / Group / Import), and a More overflow
 *  (Export Excel / Reset View / Sync Dates). No JSX — all actions fire
 *  window events that ProjectPlannerTab listens for. */

import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  CircleDot,
  Columns,
  Download,
  Filter,
  HardHat,
  ListPlus,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Search,
  Table,
  Redo2,
  Undo2,
  Upload,
} from '@/shared/ui/icons';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';

export type PlannerZoomPresetId = 'quarter' | 'month' | 'week' | 'day';
export type PlannerActivityFilter = 'all' | 'lookahead' | 'in_progress' | 'attention' | 'critical';
export type PlannerViewMode = 'table' | 'split' | 'gantt';

export interface PlannerHeaderState {
  /** Owner names available in the active programme; "all" is implicit. */
  owners: string[];
  /** Selected owner filter — "all" or one of `owners`. */
  ownerFilter: string;
  /** Operational slice of the programme shown in the table and timeline. */
  activityFilter: PlannerActivityFilter;
  /** Currently active zoom preset; null when the user is on a custom pixel value. */
  zoomPreset: PlannerZoomPresetId | null;
  /** Show / hide the page-level search input. */
  searchOpen: boolean;
  /** Read-only role gating — hides creation + secondary write actions. */
  canEdit: boolean;
  /** Disables Export Excel during a long-running export. */
  exportingExcel: boolean;
  /** Disables weekday reconciliation while it is already running. */
  adjustingWeekends: boolean;
  /** Skip-weekends toggle from the in-page toolbar. Controls Sync Dates visibility. */
  skipWeekends: boolean;
  /** True once the planner has at least one row — Export/Sync are useless otherwise. */
  hasItems: boolean;
  /** The Gantt split is mounted; adaptive list views do not need timeline controls. */
  timelineVisible: boolean;
  /** Current desktop workspace arrangement. */
  viewMode: PlannerViewMode;
  /** False on adaptive layouts and native surfaces where the Gantt is unavailable. */
  viewModeAvailable: boolean;
  /** Current date lies inside the rendered programme range. */
  todayAvailable: boolean;
  canUndo: boolean;
  canRedo: boolean;
}

export interface PlannerHeaderCtx {
  plannerHeaderState: PlannerHeaderState;
  isDesktopViewport: boolean;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
}

/** Appends Planner pills to the caller's leading/trailing action arrays.
 *  Called only when `projectDetailTab === 'planner'` and the state event
 *  has been received at least once. */
export function buildPlannerHeaderActions(ctx: PlannerHeaderCtx): void {
  const { plannerHeaderState, isDesktopViewport, leadingActions, trailingActions } = ctx;
  const { owners, ownerFilter, activityFilter, zoomPreset, searchOpen, canEdit, exportingExcel, adjustingWeekends, skipWeekends, hasItems, timelineVisible, viewMode, viewModeAvailable, todayAvailable, canUndo, canRedo } = plannerHeaderState;

  if (hasItems && timelineVisible && todayAvailable) {
    trailingActions.push({
      id: 'planner-today',
      icon: CalendarClock,
      label: 'Today',
      ariaLabel: 'Scroll timeline to today',
      onClick: () => window.dispatchEvent(new CustomEvent('planner:today')),
    });
  }

  if (hasItems && isDesktopViewport) {
    const activityViews: Array<{
      id: PlannerActivityFilter;
      label: string;
      icon: MobileHeaderSubAction['icon'];
    }> = [
      { id: 'all', label: 'All activities', icon: Filter },
      { id: 'lookahead', label: '3-week lookahead', icon: CalendarClock },
      { id: 'in_progress', label: 'In progress', icon: CircleDot },
      { id: 'attention', label: 'Needs attention', icon: AlertTriangle },
      { id: 'critical', label: 'Critical path', icon: AlertTriangle },
    ];
    const activityLabel = activityViews.find(view => view.id === activityFilter)?.label ?? 'All activities';
    const filterSubActions: MobileHeaderSubAction[] = activityViews.map(view => ({
      id: `planner-view-${view.id}`,
      icon: view.icon,
      label: view.label,
      trailingIcon: activityFilter === view.id ? Filter : undefined,
      onClick: () => window.dispatchEvent(
        new CustomEvent('planner:set-activity-filter', { detail: { filter: view.id } }),
      ),
    }));

    if (owners.length > 0) filterSubActions.push(
      {
        id: 'planner-owner-all',
        icon: HardHat,
        label: 'All Subcontractors',
        trailingIcon: ownerFilter === 'all' ? Filter : undefined,
        onClick: () => window.dispatchEvent(
          new CustomEvent('planner:set-owner-filter', { detail: { owner: 'all' } }),
        ),
      },
      ...owners.map((owner): MobileHeaderSubAction => ({
        id: `planner-owner-${owner}`,
        icon: HardHat,
        label: owner,
        trailingIcon: ownerFilter === owner ? Filter : undefined,
        onClick: () => window.dispatchEvent(
          new CustomEvent('planner:set-owner-filter', { detail: { owner } }),
        ),
      })),
    );

    const filterLabel = activityFilter !== 'all'
      ? activityLabel
      : ownerFilter === 'all'
        ? 'All'
        : ownerFilter;
    trailingActions.push({
      id: 'planner-filter',
      icon: Filter,
      label: filterLabel,
      ariaLabel: `Filter programme: ${activityLabel}${ownerFilter === 'all' ? '' : `, ${ownerFilter}`}`,
      group: 'view',
      subActions: filterSubActions,
      subActionDividerAfter: owners.length > 0 ? 'planner-view-critical' : undefined,
    });
  }

  if (hasItems && isDesktopViewport) {
    trailingActions.push({
      id: 'planner-search',
      icon: Search,
      ariaLabel: searchOpen ? 'Close search' : 'Search programme',
      active: searchOpen,
      onClick: () => window.dispatchEvent(new CustomEvent('planner:toggle-search')),
    });
  }

  if (hasItems && viewModeAvailable) {
    const viewOptions: Array<{
      id: PlannerViewMode;
      label: string;
      icon: MobileHeaderSubAction['icon'];
    }> = [
      { id: 'table', label: 'Table', icon: Table },
      { id: 'split', label: 'Split', icon: Columns },
      { id: 'gantt', label: 'Gantt', icon: BarChart3 },
    ];
    const selectedView = viewOptions.find(option => option.id === viewMode) ?? viewOptions[1];
    trailingActions.push({
      id: 'planner-view-mode',
      icon: selectedView.icon,
      label: selectedView.label,
      ariaLabel: `Planner view: ${selectedView.label}`,
      group: 'view',
      subActions: viewOptions.map(option => ({
        id: `planner-view-mode-${option.id}`,
        icon: option.icon,
        label: option.label,
        active: viewMode === option.id,
        onClick: () => window.dispatchEvent(
          new CustomEvent('planner:set-view-mode', { detail: { mode: option.id } }),
        ),
      })),
    });
  }

  if (hasItems && timelineVisible) {
    const zoomOptions: Array<{ id: PlannerZoomPresetId; label: string }> = [
      { id: 'quarter', label: 'Quarter' },
      { id: 'month',   label: 'Month' },
      { id: 'week',    label: 'Week' },
      { id: 'day',     label: 'Day' },
    ];
    const selectedZoom = zoomOptions.find(option => option.id === zoomPreset);
    trailingActions.push({
      id: 'planner-timescale',
      icon: CalendarClock,
      label: selectedZoom?.label ?? 'Scale',
      ariaLabel: selectedZoom ? `Programme timescale: ${selectedZoom.label}` : 'Choose programme timescale',
      group: 'view',
      subActions: zoomOptions.map(option => ({
        id: `planner-zoom-${option.id}`,
        label: option.label,
        active: zoomPreset === option.id,
        onClick: () => window.dispatchEvent(
          new CustomEvent('planner:set-zoom-preset', { detail: { preset: option.id } }),
        ),
      })),
    });
  }

  if (canEdit) {
    const createSubActions: MobileHeaderSubAction[] = [
      {
        id: 'planner-create-task',
        icon: Plus,
        label: 'New Task',
        onClick: () => window.dispatchEvent(new CustomEvent('planner:create-task')),
      },
      {
        id: 'planner-create-group',
        icon: ListPlus,
        label: 'New Group',
        onClick: () => window.dispatchEvent(new CustomEvent('planner:create-group')),
      },
      {
        id: 'planner-import',
        icon: Upload,
        label: 'Import from Excel…',
        onClick: () => window.dispatchEvent(new CustomEvent('planner:import')),
      },
    ];
    trailingActions.push({
      id: 'planner-create',
      icon: Plus,
      ariaLabel: 'Create task, group, or import',
      group: 'page',
      subActions: createSubActions,
    });
  }

  const overflowActions: MobileHeaderSubAction[] = [];
  if (hasItems && !isDesktopViewport) {
    overflowActions.push({
      id: 'planner-search-mobile',
      icon: Search,
      label: searchOpen ? 'Close search' : 'Search programme',
      active: searchOpen,
      onClick: () => window.dispatchEvent(new CustomEvent('planner:toggle-search')),
    });
  }
  if (hasItems) {
    overflowActions.push({
      id: 'planner-skip-weekends',
      icon: CalendarClock,
      label: 'Skip weekends',
      active: skipWeekends,
      onClick: () => window.dispatchEvent(new CustomEvent('planner:toggle-skip-weekends')),
    });
  }
  if (canEdit && canUndo) {
    overflowActions.push({
        id: 'planner-undo',
        icon: Undo2,
        label: 'Undo',
        onClick: () => window.dispatchEvent(new CustomEvent('planner:undo')),
      });
  }
  if (canEdit && canRedo) {
    overflowActions.push({
        id: 'planner-redo',
        icon: Redo2,
        label: 'Redo',
        onClick: () => window.dispatchEvent(new CustomEvent('planner:redo')),
      });
  }
  if (hasItems) {
    overflowActions.push({
      id: 'planner-export-excel',
      icon: Download,
      label: exportingExcel ? 'Exporting…' : 'Export to Excel',
      disabled: exportingExcel,
      onClick: () => window.dispatchEvent(new CustomEvent('planner:export-excel')),
    });
  }
  if (hasItems && timelineVisible) {
    overflowActions.push({
      id: 'planner-reset-view',
      icon: RotateCcw,
      label: 'Reset View',
      onClick: () => window.dispatchEvent(new CustomEvent('planner:reset-view')),
    });
  }
  if (canEdit && skipWeekends && hasItems) {
    overflowActions.push({
      id: 'planner-sync-dates',
      icon: CalendarClock,
      label: adjustingWeekends ? 'Syncing dates…' : 'Sync dates to weekdays',
      disabled: adjustingWeekends,
      onClick: () => window.dispatchEvent(new CustomEvent('planner:sync-dates')),
    });
  }
  if (overflowActions.length > 0) {
    trailingActions.push({
      id: 'planner-more',
      icon: MoreHorizontal,
      ariaLabel: 'More actions',
      group: 'page',
      subActions: overflowActions,
    });
  }
}
